<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  sidePreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const footerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.FOOTER);
});

const buttonsComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BUTTONS);
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const renderedHeaderText = computed(() => {
  if (headerComponent.value?.format !== 'TEXT') return '';
  return replaceTemplateVariables(
    headerComponent.value?.text || '',
    processedParams.value
  );
});

const footerText = computed(() => footerComponent.value?.text || '');

const templateButtons = computed(() => buttonsComponent.value?.buttons || []);

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div v-if="sidePreview" class="grid h-full min-h-0 gap-5 lg:grid-cols-[minmax(0,1fr)_27rem]">
    <section class="min-h-0 overflow-y-auto pr-1">
      <slot name="before-form" />

      <div v-if="hasVariables || hasMediaHeader" class="space-y-4">
        <div v-if="hasMediaHeader">
          <p class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
            {{
              $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
                type: formatType,
              }) || `${formatType} Header`
            }}
          </p>
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="w-full"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
          <Input
            v-if="isDocumentTemplate"
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="mt-2 w-full"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>

        <div v-if="processedParams.body">
          <p class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
            Variáveis da mensagem
          </p>
          <div class="grid gap-2">
            <label
              v-for="(variable, key) in processedParams.body"
              :key="`body-${key}`"
              class="block"
            >
              <span class="mb-1 block text-xs text-n-slate-10">
                Variável {{ key }}
              </span>
              <Input
                v-model="processedParams.body[key]"
                type="text"
                class="w-full"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                    variable: key,
                  })
                "
              />
            </label>
          </div>
        </div>

        <div v-if="processedParams.buttons">
          <p class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
            {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
          </p>
          <div class="grid gap-2">
            <Input
              v-for="(button, index) in processedParams.buttons"
              :key="`button-${index}`"
              v-model="processedParams.buttons[index].parameter"
              type="text"
              class="w-full"
              :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
            />
          </div>
        </div>
      </div>

      <p v-else class="rounded-md bg-n-alpha-2 p-3 text-sm text-n-slate-11">
        Este template não tem variáveis. Confira o preview ao lado e envie.
      </p>

      <p
        v-if="v$.$dirty && v$.$invalid"
        class="mt-3 rounded-md bg-n-ruby-9/20 p-2.5 text-center text-sm text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>

      <div class="mt-4">
        <slot
          name="actions"
          :send-message="sendMessage"
          :reset-template="resetTemplate"
          :go-back="goBack"
          :is-valid="!v$.$invalid"
          :disabled="isFormInvalid"
        />
      </div>
    </section>

    <aside class="flex h-full min-h-0 flex-col rounded-lg border border-solid border-n-weak bg-[#efe7dd] p-4">
      <p class="mb-3 text-xs font-semibold uppercase text-n-slate-10">
        Preview
      </p>
      <div class="min-h-0 flex-1 rounded-lg bg-[#efe7dd]">
        <div class="h-full min-h-0 overflow-y-auto pr-1">
          <div class="ml-auto max-h-full max-w-[23rem] overflow-y-auto rounded-lg bg-white px-3 py-2 text-[0.82rem] leading-5 text-[#111b21] shadow-sm">
            <p
              v-if="renderedHeaderText"
              class="mb-2 font-semibold whitespace-pre-wrap"
            >
              {{ renderedHeaderText }}
            </p>
            <div
              v-else-if="hasMediaHeader"
              class="mb-2 rounded-md bg-[#f0f2f5] px-3 py-5 text-center text-xs text-[#667781]"
            >
              {{ formatType }}
            </div>
            <p class="whitespace-pre-wrap">
              {{ renderedTemplate }}
            </p>
            <p
              v-if="footerText"
              class="mt-2 whitespace-pre-wrap text-xs text-[#667781]"
            >
              {{ footerText }}
            </p>
            <div
              v-if="templateButtons.length"
              class="mt-2 divide-y divide-[#e9edef] border-t border-[#e9edef]"
            >
              <p
                v-for="(button, index) in templateButtons"
                :key="`${button.text}-${index}`"
                class="py-2 text-center text-xs font-medium text-[#00a884]"
              >
                {{ button.text }}
              </p>
            </div>
            <p class="mt-1 text-right text-[0.68rem] text-[#667781]">21:28</p>
          </div>
        </div>
      </div>
    </aside>
  </div>

  <div v-else>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
